import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Pane,
  PaneMenu,
  Paneset,
  SearchField,
} from '@folio/stripes-components';
import {
  SearchAndSortQuery,
  SearchAndSortNoResultsMessage as NoResultsMessage,
  CollapseFilterPaneButton,
  ExpandFilterPaneButton,
} from '@folio/stripes/smart-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppIcon } from '@folio/stripes-core';
import TranslationFilters from '../Filters/TranslationFilters';
import ResultsPane from '../ResultsPane/ResultsPane';
import ActionsMenu from '../ActionsMenu/ActionsMenu';
import TranslationsStats from '../Statistics/TranslationsStats';
import { useTranslationStats } from '../../Hooks/useTranslationStats';

/**
 * TranslationWrapper — View layer. Follows ReportsList pattern exactly.
 *
 * Uses SearchAndSortQuery which:
 *  - writes all filters/search to URL via querySetter
 *  - provides resetAll, clearGroup, getFilterHandlers() automatically
 *  - no manual writeParams needed
 */
function TranslationWrapper({
  data = {},
  onNeedMoreData,
  queryGetter,
  querySetter,
  initialSearch,
  languages = [],
  apps = [],
  tables = [],
  categoryName,
  paneTitle,
  activeFilterKeys = ['locale', 'app', 'table', 'status'],
  isAssignedTranslator = false,
  mutator = {},
  resources = {},
}) {
  const intl = useIntl();

  const [filterPaneIsVisible, setFilterPaneIsVisible] = useState(true);
  const [showStats, setShowStats]                     = useState(true);
  const [selection, setSelection]                     = useState({});

  const { translationKeys = [], totalCount = 0, isLoading = false } = data;

  const query       = queryGetter ? queryGetter() || {} : {};
  const filtersStr  = query.filters || '';
  const activeMap   = {};
  filtersStr.split(',').filter(Boolean).forEach((f) => {
    const dot = f.indexOf('.');
    if (dot === -1) return;
    const name  = f.substring(0, dot);
    const value = f.substring(dot + 1);
    if (!activeMap[name]) activeMap[name] = [];
    activeMap[name].push(value);
  });
  const currentLocale = activeMap.localeValue?.[0] || '';

  // ── Stats ─────────────────────────────────────────────────────────
  const { data: statsData } = useTranslationStats({
    categoryName,
    localeValue: currentLocale,
    enabled: !!currentLocale,
  });

  // ── Selected for ActionsMenu ──────────────────────────────────────
  const selectedTranslations = useMemo(
    () => translationKeys.filter((row) => selection[row.translationKey]),
    [translationKeys, selection],
  );

  const activeFilterCount = filtersStr.split(',').filter(Boolean).length;

  const paneSub = isLoading ? (
    <FormattedMessage id="stripes-smart-components.searchCriteria" />
  ) : (
    <FormattedMessage
      id="stripes-smart-components.searchResultsCountHeader"
      values={{ count: totalCount }}
    />
  );

  return (
    <SearchAndSortQuery
      querySetter={querySetter}
      queryGetter={queryGetter}
      initialSearch={initialSearch}
      initialSearchState={{ query: '' }}
    >
      {({
        searchValue,
        getSearchHandlers,
        onSubmitSearch,
        getFilterHandlers,
        activeFilters,
        filterChanged,
        searchChanged,
        resetAll,
      }) => (
        <Paneset>
          {/* ── Filter Pane ───────────────────────────────────── */}
          {filterPaneIsVisible && (
            <Pane
              defaultWidth="22%"
              paneTitle={paneTitle}
              lastMenu={
                <PaneMenu>
                  <CollapseFilterPaneButton onClick={() => setFilterPaneIsVisible(false)} />
                </PaneMenu>
              }
            >
              <form onSubmit={onSubmitSearch}>
                <SearchField
                  aria-label={intl.formatMessage({
                    id: 'ui-translations.meta.title',
                    defaultMessage: 'Translations',
                  })}
                  autoFocus
                  autoComplete="off"
                  name="query"
                  id="input-translation-search"
                  onChange={(e) => {
                    if (e.target.value) getSearchHandlers().query(e);
                    else getSearchHandlers().reset();
                  }}
                  value={searchValue.query}
                  marginBottom0
                />
                <div style={{ marginBottom: '0.5rem' }} />
                <Button
                  buttonStyle="none"
                  id="clickable-reset-all-translations"
                  disabled={!(filterChanged || searchChanged)}
                  fullWidth
                  onClick={resetAll}
                >
                  <Icon icon="times-circle-solid">
                    <FormattedMessage id="stripes-smart-components.resetAll" />
                  </Icon>
                </Button>
                <div style={{ marginBottom: '1rem' }} />
                <TranslationFilters
                  activeFilters={activeFilters.state}
                  onChangeHandlers={getFilterHandlers()}
                  resultOffset={mutator.resultOffset}
                  languages={languages}
                  apps={apps}
                  tables={tables}
                  activeFilterKeys={activeFilterKeys}
                />
              </form>
            </Pane>
          )}

          {/* ── Results Pane ──────────────────────────────────── */}
          <Pane
            id="translation-results-pane"
            appIcon={<AppIcon app="translations" />}
            defaultWidth="fill"
            padContent={false}
            noOverflow
            paneTitle={paneTitle}
            paneSub={paneSub}
            firstMenu={
              !filterPaneIsVisible ? (
                <PaneMenu>
                  <ExpandFilterPaneButton
                    filterCount={activeFilterCount}
                    onClick={() => setFilterPaneIsVisible(true)}
                  />
                </PaneMenu>
              ) : null
            }
            lastMenu={
              <ActionsMenu
                currentLocale={currentLocale}
                categoryName={categoryName}
                contentData={translationKeys}
                selectedTranslations={selectedTranslations}
                currentStatus={activeMap.status?.[0] || 'allTranslations'}
                fileName={`${intl.formatMessage({
                  id: `ui-translations.category.${categoryName}`,
                  defaultMessage: categoryName,
                })} - ${currentLocale}`}
              />
            }
          >
            {!currentLocale ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Icon icon="arrow-left">
                  <FormattedMessage id="ui-translations.localeTranslations.noLanguageSelected" />
                </Icon>
              </div>
            ) : (
              <ResultsPane
                contentData={translationKeys}
                totalCount={totalCount}
                onNeedMoreData={onNeedMoreData}
                currentLocale={currentLocale}
                category={categoryName}
                searchTerm={searchValue.query || ''}
                searchActive={searchValue.query ? 'true' : 'false'}
                selection={selection}
                setSelection={setSelection}
                isAssignedTranslator={isAssignedTranslator}
              />
            )}
          </Pane>

          {/* ── Stats Pane ────────────────────────────────────── */}
          {showStats && currentLocale && (
            <Pane
              appIcon={<AppIcon app="erm-usage" />}
              defaultWidth="20%"
              paneTitle={<FormattedMessage id="ui-translations.pane.Statistics" />}
              firstMenu={
                <PaneMenu>
                  <Button buttonStyle="dropdownItem" marginBottom0 onClick={() => setShowStats(false)}>
                    <Icon icon="chevron-double-right" />
                  </Button>
                </PaneMenu>
              }
            >
              <TranslationsStats
                statsData={statsData}
                currentLocale={currentLocale}
                categoryName={categoryName}
              />
            </Pane>
          )}
          {!showStats && currentLocale && (
            <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '0.5rem' }}>
              <Button buttonStyle="dropdownItem" marginBottom0 onClick={() => setShowStats(true)}>
                <Icon icon="chevron-double-left" />
              </Button>
            </div>
          )}
        </Paneset>
      )}
    </SearchAndSortQuery>
  );
}

TranslationWrapper.propTypes = {
  data: PropTypes.shape({
    translationKeys: PropTypes.arrayOf(PropTypes.object),
    totalCount:      PropTypes.number,
    isLoading:       PropTypes.bool,
  }),
  onNeedMoreData:       PropTypes.func,
  queryGetter:          PropTypes.func,
  querySetter:          PropTypes.func,
  initialSearch:        PropTypes.string,
  languages:            PropTypes.arrayOf(PropTypes.object),
  apps:                 PropTypes.arrayOf(PropTypes.string),
  tables:               PropTypes.arrayOf(PropTypes.string),
  categoryName:         PropTypes.string.isRequired,
  paneTitle:            PropTypes.node,
  activeFilterKeys:     PropTypes.arrayOf(PropTypes.string),
  isAssignedTranslator: PropTypes.bool,
  mutator:              PropTypes.object,
  resources:            PropTypes.object,
};

export default TranslationWrapper;
